import { useCallback, useEffect, useRef, useState } from "react";

const QuizTimer = ({
  seconds,
  handleSubmitQuiz,
  isEnded,
  startedTimestamp,
  quizDuration,
}: {
  seconds: number;
  handleSubmitQuiz: () => void;
  isEnded: boolean;
  startedTimestamp?: string | null;
  quizDuration?: number | null;
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(seconds);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    // Don't start timer if no startedTimestamp or quiz has ended
    if (!startedTimestamp || isEnded) return;

    const startTimestamp = Number(startedTimestamp);
    const currentTime = Date.now();

    // Validate timestamp - ensure it's not in the future and not too old
    if (isNaN(startTimestamp) || startTimestamp > currentTime) {
      console.error("Invalid quiz start timestamp:", startedTimestamp);
      return;
    }

    // Calculate initial elapsed time
    const elapsedSeconds = Math.floor((currentTime - startTimestamp) / 1000);
    const initialRemaining = quizDuration - elapsedSeconds;

    // Ensure timer has actually started (at least 1 second must have passed)
    if (elapsedSeconds < 1) {
      console.log("Quiz timer starting, waiting for first second to pass...");
      setRemainingSeconds(quizDuration);
    } else {
      setRemainingSeconds(Math.max(0, initialRemaining));
    }

    timerRef.current = setInterval(() => {
      const currentTimestamp = Date.now();
      const newRemainingSeconds =
        quizDuration -
        Math.floor((currentTimestamp - startTimestamp) / 1000);

      if (newRemainingSeconds <= 0) {
        clearInterval(timerRef.current);
        setRemainingSeconds(0);

        // Only submit once and only if timer has actually run
        if (!hasSubmitted.current) {
          const totalElapsed = Math.floor((currentTimestamp - startTimestamp) / 1000);
          console.log(`Quiz time expired. Total elapsed: ${totalElapsed}s`);
          hasSubmitted.current = true;
          handleSubmitQuiz();
        }
      } else {
        setRemainingSeconds(newRemainingSeconds);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [startedTimestamp, quizDuration, isEnded]);

  useEffect(() => {
    if (isEnded) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [isEnded]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }, []);

  return <>{formatTime(remainingSeconds)}</>;
};

export default QuizTimer;
